import { ColorToken, DonutChart, DonutSegment } from '@backrs_ui/common-ui';

import { EntryTone } from '@backrs_ui/common-ui/src/molecules/MonthCalendar/monthCalendar.types';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  BoosterPhase,
  PlanSection,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

import { toneForSection } from '../utils/planCalendar';

const STRINGS = appStrings.modules.satActBooster.results.studyProfile;

// Mirror the study-plan calendar chips' per-section border color (the 200-level tone).
const TONE_BG: Record<EntryTone, ColorToken> = {
  brand: 'Brand/200',
  indigo: 'Indigo/200',
  blue: 'Blue/200',
  orange: 'Orange/200',
  teal: 'Teal/200',
  pink: 'Pink/200',
  green: 'Green/200',
  yellow: 'Yellow/200',
  neutral: 'Colors/Border/border-secondary',
};

type StudyProfileCardProps = Readonly<{
  /** The time-to-test phase; a known code renders a chip, null (or an unknown code) renders none. */
  phase: BoosterPhase | null;
  /** Per-section study-time allocation; rendered as a donut, highest share first. */
  sections: readonly PlanSection[];
}>;

const clampShare = (share: number): number => Math.max(0, Math.min(100, share));

/**
 * "Your Study Profile": how study time is split across sections, shown as a donut with a legend
 * (highest share first). Renders nothing when there is neither an allocation nor a known phase.
 */
export const StudyProfileCard = ({ phase, sections }: StudyProfileCardProps) => {
  const phaseLabel = phase ? STRINGS.phases[phase] : undefined;
  const segments: DonutSegment[] = [...sections]
    .sort((a, b) => b.share - a.share)
    .map((section) => ({
      label: section.section,
      value: clampShare(section.share),
      color: TONE_BG[toneForSection(section.section)],
    }));

  if (segments.length === 0 && !phaseLabel) return null;

  return (
    <Box sx={cardSx} id="booster-study-profile-card">
      <Box sx={headingSx}>
        <Typography variant="text-sm-semibold" sx={headingTextSx}>
          {STRINGS.label}
        </Typography>
      </Box>

      <Box sx={contentSx}>
        {phaseLabel && <Chip size="md" variant="badge" label={phaseLabel} sx={phaseChipSx} />}

        {segments.length > 0 && (
          <Box sx={chartRowSx}>
            <Box sx={donutWrapSx}>
              <DonutChart variant={{ type: 'multi', segments }} size="small" />
            </Box>
            <Box sx={legendSx}>
              {segments.map((segment) => (
                <Box key={segment.label} sx={legendRowSx}>
                  <Box sx={legendDotSx(segment.color)} />
                  <Typography variant="text-sm-medium" sx={sectionNameSx}>
                    {segment.label}
                  </Typography>
                  <Typography variant="text-sm-semibold" sx={sharePctSx}>
                    {Math.round(segment.value)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: backrs.spacing['spacing-lg'],
  paddingBottom: backrs.spacing['spacing-md'],
  paddingLeft: backrs.spacing['spacing-2xl'],
  paddingRight: backrs.spacing['spacing-2xl'],
});

const headingTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  gap: backrs.spacing['spacing-2xl'],
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const phaseChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  height: 'auto',
  alignSelf: 'flex-start',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    color: backrs.colors['Colors/Text/text-secondary'],
    paddingInline: backrs.spacing['spacing-md'],
    paddingBlock: backrs.spacing['spacing-xxs'],
  },
});

const chartRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-3xl'],
});

const donutWrapSx: SxProps<Theme> = {
  flexShrink: 0,
};

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0',
  minWidth: '140px',
  gap: backrs.spacing['spacing-lg'],
});

const legendRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const legendDotSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '10px',
    height: '10px',
    flexShrink: 0,
    borderRadius: backrs.radius['radius-full'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: backrs.colors[color],
  });

const sectionNameSx: SxProps<Theme> = ({ backrs }) => ({
  flexGrow: 1,
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const sharePctSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
